import { api } from './client';
import { compressImage } from '@/lib/compressImage';

export interface SisCustomField {
  label: string;
  value: string;
}

/** Row in the store list (Assets Master landing). */
export interface SisAssetStore {
  id: number;
  name: string;
  city: string;
  group: string;
  lengthFt: number | null;
  widthFt: number | null;
  areaSqft: number | null;
  salesman: string;
  salary: number | null;
  assetLines: number;
}

export interface SisAsset {
  id: number;
  assetType: string;
  quantity: number;
  material: string;
  size: string;
  remarks: string;
  customFields: SisCustomField[];
  updatedAt: string;
}

export interface SisStoreProfile {
  id: number;
  name: string;
  group: string;
  address: string;
  city: string;
  lengthFt: number | null;
  widthFt: number | null;
  areaSqft: number | null;
  salesman: string;
  salary: number | null;
  /** Up to 5 photos of the store. */
  photoUrls: string[];
}

export interface SisStoreDetail {
  store: SisStoreProfile;
  assets: SisAsset[];
}

export interface SisStoreProfilePayload {
  lengthFt?: number;
  widthFt?: number;
  areaSqft?: number;
  salesman?: string;
  salary?: number;
  address?: string;
  city?: string;
}

export interface SisAssetPayload {
  partyId?: number;
  assetType: string;
  quantity: number;
  material?: string;
  size?: string;
  remarks?: string;
  customFields?: SisCustomField[];
}

export async function listSisAssetStores(): Promise<SisAssetStore[]> {
  const r = await api.get<SisAssetStore[]>('/sis-assets/stores');
  return r.data;
}

export async function getSisStore(partyId: number): Promise<SisStoreDetail> {
  const r = await api.get<SisStoreDetail>(`/sis-assets/store/${partyId}`);
  return r.data;
}

export async function updateSisStoreProfile(
  partyId: number,
  payload: SisStoreProfilePayload,
): Promise<{ id: number }> {
  const r = await api.patch(`/sis-assets/store/${partyId}`, payload);
  return r.data;
}

/** Max photos per SIS store (mirrors the server). */
export const MAX_STORE_PHOTOS = 5;

export async function uploadSisStorePhotos(partyId: number, files: (File | Blob)[]): Promise<{ photoUrls: string[] }> {
  const fd = new FormData();
  // Shrink each photo first so pages and documents stay light.
  for (const f of await Promise.all(files.map((x) => compressImage(x)))) fd.append('image', f, f instanceof File ? f.name : 'store.png');
  const r = await api.post(`/sis-assets/store/${partyId}/photo`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return r.data;
}

export async function deleteSisStorePhoto(partyId: number, url: string): Promise<{ photoUrls: string[] }> {
  const r = await api.delete(`/sis-assets/store/${partyId}/photo`, { params: { url } });
  return r.data;
}

export async function createSisAsset(payload: SisAssetPayload): Promise<{ id: number }> {
  const r = await api.post('/sis-assets', payload);
  return r.data;
}

export async function updateSisAsset(id: number, payload: SisAssetPayload): Promise<{ id: number }> {
  const r = await api.patch(`/sis-assets/${id}`, payload);
  return r.data;
}

export async function deleteSisAsset(id: number): Promise<{ id: number }> {
  const r = await api.delete(`/sis-assets/${id}`);
  return r.data;
}
